package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalStrings
import com.milta.feature.admin.domain.entity.AdminPrompt
import com.milta.feature.admin.domain.usecase.GetAdminPromptsUseCase
import com.milta.feature.admin.domain.usecase.ResetPromptUseCase
import com.milta.feature.admin.domain.usecase.UpdatePromptUseCase
import com.milta.feature.admin.presentation.AdminPromptsIntent
import com.milta.feature.admin.presentation.AdminPromptsState
import com.milta.feature.admin.presentation.AdminPromptsViewModel
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.koin.compose.koinInject

private const val LockBg = "#EEF1F3"
private const val LockInk = "#5A6E78"

/**
 * Admin "Prompts" screen (2026-10-03) — one tab per live Claude prompt. Only the editable body
 * ever round-trips to the backend (PUT/reset); the locked contract footer shown below the editor
 * is always the server's own value, display-only, never part of any save request — mirrors the
 * backend split (PromptTemplate stores nothing but the body).
 *
 * Markdown toolbar commands append/prefix a snippet at the end of the draft rather than at the
 * caret — Compose HTML's controlled TextArea exposes no element ref / selectionStart-End API in
 * the version used here, so caret-aware insertion (as in the validated mockup's raw-DOM textarea)
 * isn't reproducible; this is a deliberate, scoped-down simplification.
 */
@Composable
fun AdminPromptsContent() {
    val getAdminPrompts: GetAdminPromptsUseCase = koinInject()
    val updatePrompt: UpdatePromptUseCase = koinInject()
    val resetPrompt: ResetPromptUseCase = koinInject()
    val viewModel = remember { AdminPromptsViewModel(getAdminPrompts, updatePrompt, resetPrompt) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) { viewModel.onIntent(AdminPromptsIntent.Load) }

    val strings = LocalStrings.current.adminPrompts

    when {
        state.isLoading -> Div(attrs = { style { padding(40.px); property("display", "flex"); property("align-items", "center") } }) {
            WebSpinner()
            Div(attrs = { style { padding(0.px, 0.px, 0.px, 12.px); property("color", WebTextSecondary); fontSize(13.5.px) } }) { Text(strings.loading) }
        }
        state.error != null -> Div(
            attrs = { style { padding(16.px); property("background", WebErrorBg); property("color", WebErrorInk); property("border-radius", "10px") } },
        ) { Text(if (state.error == "offline") strings.errorNoConnection else strings.errorGeneric) }
        else -> AdminPromptsBody(state = state, onIntent = viewModel::onIntent)
    }
}

@Composable
private fun AdminPromptsBody(state: AdminPromptsState, onIntent: (AdminPromptsIntent) -> Unit) {
    val strings = LocalStrings.current.adminPrompts
    Div(attrs = {
        style {
            property("display", "flex"); property("align-items", "center"); property("gap", "8px")
            property("background", WebSurface); property("border", "1px solid $WebBorder")
            property("border-radius", "10px"); padding(9.px, 14.px); property("margin-bottom", "18px")
            fontSize(12.5.px); property("color", WebTextSecondary)
        }
    }) {
        Text(strings.contractLockedNotice)
    }

    Div(attrs = { style { property("display", "flex"); property("gap", "4px"); property("border-bottom", "1px solid $WebBorder"); property("margin-bottom", "18px"); property("overflow-x", "auto") } }) {
        state.prompts.forEach { prompt ->
            val isActive = prompt.key == state.selectedKey
            Div(attrs = {
                onClick { onIntent(AdminPromptsIntent.SelectTab(prompt.key)) }
                style {
                    padding(10.px, 16.px); fontSize(13.5.px); property("font-weight", "600"); property("white-space", "nowrap")
                    property("cursor", "pointer"); property("border-bottom", if (isActive) "2.5px solid $WebTeal" else "2.5px solid transparent")
                    property("color", if (isActive) WebPrimary else WebTextSecondary)
                }
            }) { Text(prompt.label) }
        }
    }

    val selected = state.prompts.firstOrNull { it.key == state.selectedKey }
    if (selected != null) {
        PromptPanel(prompt = selected, state = state, onIntent = onIntent)
    }
}

@Composable
private fun PromptPanel(prompt: AdminPrompt, state: AdminPromptsState, onIntent: (AdminPromptsIntent) -> Unit) {
    val strings = LocalStrings.current.adminPrompts
    val key = prompt.key
    val draft = state.drafts[key] ?: prompt.body
    val isDirty = draft != prompt.body
    val isSaving = key in state.savingKeys
    val showPreview = key in state.previewKeys

    // Compose HTML's controlled TextArea exposes no selectionStart/selectionEnd (no DOM element
    // ref API in this Compose Web version) — toolbar commands append/prefix at the end of the
    // draft instead of at the caret, a deliberate simplification vs. the validated mockup (which
    // manipulated a raw, uncontrolled <textarea> directly).
    fun applyCommand(cmd: String) {
        fun append(snippet: String) {
            val separator = if (draft.isEmpty() || draft.endsWith("\n")) "" else "\n"
            onIntent(AdminPromptsIntent.UpdateDraft(key, draft + separator + snippet))
        }
        when (cmd) {
            "bold" -> append(strings.markdownBoldSnippet)
            "italic" -> append(strings.markdownItalicSnippet)
            "code" -> append(strings.markdownCodeSnippet)
            "heading" -> append(strings.markdownHeadingSnippet)
            "ul" -> append(strings.markdownBulletSnippet)
            "ol" -> append(strings.markdownNumberedSnippet)
            "quote" -> append(strings.markdownQuoteSnippet)
        }
    }

    // Meta bar
    Div(attrs = {
        style {
            property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("flex-wrap", "wrap"); property("gap", "10px")
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); padding(12.px, 16.px)
            property("margin-bottom", "16px"); property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)")
        }
    }) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("flex-wrap", "wrap") } }) {
            Div(attrs = {
                style {
                    property("font-family", "monospace"); fontSize(11.5.px); property("font-weight", "600"); property("color", WebPrimary)
                    property("background", WebActiveTint); padding(4.px, 9.px); property("border-radius", "6px")
                }
            }) { Text(prompt.endpoint) }
            Div(attrs = { style { fontSize(12.px); property("color", WebTextSecondary) } }) {
                Text(if (prompt.isOverridden) "${strings.modifiedOnPrefix} ${formatDate(prompt.updatedAt)}" else strings.notModifiedLabel)
            }
        }
        if (prompt.isOverridden) {
            Div(attrs = {
                onClick { if (!isSaving) onIntent(AdminPromptsIntent.Reset(key)) }
                style {
                    fontSize(12.5.px); property("font-weight", "600"); property("color", WebError)
                    property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                    padding(8.px, 14.px); property("cursor", "pointer")
                }
            }) { Text(strings.resetToDefaultAction) }
        }
    }

    Div(attrs = { style { fontSize(11.5.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); property("margin-bottom", "8px") } }) {
        Text(strings.editableBodyLabel)
    }

    Div(attrs = {
        style {
            property("border", "1px solid $WebBorder"); property("border-radius", "12px"); property("background", WebSurface)
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("overflow", "hidden")
        }
    }) {
        // Toolbar
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); padding(6.px, 8.px); property("border-bottom", "1px solid $WebBorder"); property("background", WebSurfaceDim) } }) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "2px"); if (showPreview) property("visibility", "hidden") } }) {
                ToolbarButton("H") { applyCommand("heading") }
                ToolbarButton("B", bold = true) { applyCommand("bold") }
                ToolbarButton("i", italic = true) { applyCommand("italic") }
                ToolbarButton("•≡") { applyCommand("ul") }
                ToolbarButton("1.") { applyCommand("ol") }
                ToolbarButton("❝") { applyCommand("quote") }
                ToolbarButton("</>") { applyCommand("code") }
            }
            Div(attrs = {
                onClick { onIntent(AdminPromptsIntent.TogglePreview(key)) }
                style {
                    fontSize(12.5.px); property("font-weight", "600"); property("color", WebTextSecondary)
                    padding(6.px, 12.px); property("border-radius", "7px"); property("cursor", "pointer"); property("border", "1px solid $WebBorder")
                }
            }) { Text(if (showPreview) strings.previewToggleEdit else strings.previewToggleShow) }
        }

        if (showPreview) {
            MarkdownPreview(draft)
        } else {
            TextArea(value = draft, attrs = {
                onInput { event -> onIntent(AdminPromptsIntent.UpdateDraft(key, event.value)) }
                style {
                    width(100.percent)
                    height(360.px)
                    property("resize", "vertical"); property("font-family", "monospace"); fontSize(13.px); property("line-height", "1.6")
                    padding(14.px, 16.px); property("border", "none"); property("outline", "none"); property("color", WebTextPrimary)
                    property("box-sizing", "border-box")
                }
            })
        }
    }

    Div(attrs = { style { fontSize(11.5.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); property("margin", "18px 0 8px") } }) {
        Text(strings.contractFooterLabel)
    }
    Div(attrs = {
        style {
            property("font-family", "monospace"); fontSize(12.5.px); property("line-height", "1.7"); property("white-space", "pre-wrap")
            property("background", LockBg); property("color", LockInk); property("border", "1px dashed $WebBorder"); property("border-radius", "12px")
            padding(16.px)
        }
    }) { Text(prompt.contractFooter) }

    val actionError = state.actionErrors[key]
    if (actionError != null) {
        Div(attrs = { style { property("margin-top", "10px"); fontSize(12.5.px); property("color", WebErrorInk) } }) {
            Text(if (actionError == "offline") strings.actionErrorOffline else strings.actionErrorGeneric)
        }
    }

    Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-top", "16px") } }) {
        Div(attrs = { style { fontSize(12.px); property("color", WebWarning); property("font-weight", "600"); property("visibility", if (isDirty) "visible" else "hidden") } }) {
            Text(strings.unsavedChangesNotice)
        }
        Div(attrs = {
            if (isDirty && !isSaving) onClick { onIntent(AdminPromptsIntent.Save(key)) }
            style {
                fontSize(12.5.px); property("font-weight", "600"); property("border-radius", "8px"); padding(9.px, 16.px)
                if (isDirty && !isSaving) {
                    property("cursor", "pointer"); property("background", WebPrimary); property("border", "1px solid $WebPrimary"); property("color", "#FFFFFF")
                } else {
                    property("cursor", "not-allowed"); property("background", WebSurfaceDim); property("border", "1px solid $WebBorder"); property("color", WebTextSecondary)
                }
            }
        }) { Text(if (isSaving) strings.savingAction else strings.saveAction) }
    }
}

@Composable
private fun ToolbarButton(label: String, bold: Boolean = false, italic: Boolean = false, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            width(30.px); height(30.px); property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("border-radius", "7px"); fontSize(if (label.length > 1) 11.5.px else 13.5.px); property("font-weight", if (bold) "700" else "500")
            if (italic) property("font-style", "italic")
            property("color", WebTextSecondary); property("cursor", "pointer")
        }
    }) { Text(label) }
}

/** Block-level only (headings/lists/quotes/paragraphs) — no inline bold/italic rendering: Compose
 * HTML has no rich-text span primitive already in use elsewhere in this codebase (every screen here
 * is built from plain Div/Text, see DashboardShell's own doc), so inline emphasis markers are
 * stripped rather than rendered, instead of introducing an unproven API for this one screen. */
@Composable
private fun MarkdownPreview(markdown: String) {
    val headingRegex = Regex("^(#{2,4})\\s+(.*)")
    val bulletRegex = Regex("^[-*]\\s+(.*)")
    val numberedRegex = Regex("^\\d+\\.\\s+(.*)")
    val quoteRegex = Regex("^>\\s?(.*)")

    Div(attrs = { style { padding(14.px, 18.px); fontSize(13.5.px); property("line-height", "1.65"); property("max-height", "420px"); property("overflow-y", "auto") } }) {
        for (line in markdown.split("\n")) {
            val heading = headingRegex.find(line)
            val bullet = bulletRegex.find(line)
            val numbered = numberedRegex.find(line)
            val quote = quoteRegex.find(line)
            when {
                heading != null -> {
                    val level = heading.groupValues[1].length
                    Div(attrs = { style { property("font-weight", "700"); property("color", WebPrimary); fontSize((20 - level).px); property("margin", "12px 0 6px") } }) {
                        Text(stripInlineMarkers(heading.groupValues[2]))
                    }
                }
                bullet != null -> Div(attrs = { style { property("padding-left", "18px"); property("margin-bottom", "4px") } }) {
                    Text("• " + stripInlineMarkers(bullet.groupValues[1]))
                }
                numbered != null -> Div(attrs = { style { property("padding-left", "4px"); property("margin-bottom", "4px") } }) {
                    Text(stripInlineMarkers(line))
                }
                quote != null -> Div(attrs = {
                    style {
                        property("border-left", "3px solid $WebTeal"); padding(4.px, 12.px); property("color", WebTextSecondary)
                        property("background", WebSurfaceDim); property("border-radius", "0 6px 6px 0"); property("margin", "6px 0")
                    }
                }) { Text(stripInlineMarkers(quote.groupValues[1])) }
                line.isBlank() -> Div(attrs = { style { height(8.px) } })
                else -> Div(attrs = { style { property("margin-bottom", "8px") } }) { Text(stripInlineMarkers(line)) }
            }
        }
    }
}

private fun stripInlineMarkers(text: String) = text.replace("**", "").replace("`", "")

/** "2026-10-03T14:35:10+00:00" -> "03/10/2026". Same string-split approach used elsewhere in this
 * module (AdminUsersScreen.formatDate, WebHistoriqueScreen.formatDateDdMmYyyy). */
private fun formatDate(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    val datePart = iso.substringBefore('T')
    val parts = datePart.split("-")
    return if (parts.size == 3) "${parts[2]}/${parts[1]}/${parts[0]}" else iso
}
